import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from 'react';
import { Button, Form } from '@bedrock/components';
import { isEqual } from 'lodash';
import { useControllableValue, useMemoizedFn, useDebounceEffect } from 'ahooks';
import classNames from 'classnames/bind';
import {
  PropertyRefDto,
  DemandCooperatePropertyValueDto,
  commonFilterOptions,
  DemandCooperatePropertiesVo,
  TemplateStandardItemDto,
} from '@mpc/common/src/services/api';
import { $sType } from '@mpc/common';
import styles from './index.less';
import FormItemDescriptions, {
  FormItemDescriptionsItemType,
} from '#components/DescriptionsFormItem';
import { getRequiredRule, getOptionsRule, getLogicOptions } from '#features/utils';

const cx = classNames.bind(styles);

type FormValueItem = Record<string, any>;

type CooperationFormListProps = {
  schema?: DemandCooperatePropertiesVo;
  view?: boolean; // 是否是编辑类型
  value?: DemandCooperatePropertyValueDto[];
  onChange?: (value: DemandCooperatePropertyValueDto[]) => void;
  editType?: 'default' | 'returnToBusiness'; // 编辑类型
  structuredVersion?: number;
};

// formValue转换为合作内容值
const formValueToCooperationValue = (
    formValue: FormValueItem,
    schema: DemandCooperatePropertiesVo
) => {
  const standard_values =
      schema?.standards?.map?.((schemaItem) => {
        const value = formValue?.[schemaItem.code] ?? [];
        return {
          property_code: schemaItem.code,
          standard_schema: schemaItem,
          value: Array.isArray(value) ? value : [value],
          type: 1,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  const property_values =
      schema?.properties?.map?.((schemaItem) => {
        const value = formValue?.[schemaItem.property_code] ?? [];
        return {
          property_code: schemaItem.property_code,
          property_schema: schemaItem,
          value: Array.isArray(value) ? value : [value],
          type: 0,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  return standard_values.concat(property_values);
};

// 根据property_type修改数据模型，如果property_type为dropdown_list或者date_range，则value为数组，否则为单个值
const modifyDataModel = (properties: PropertyRefDto[], values: Record<string, any>) => {
  const newValues = {...values};
  const arrCodeMap = {}
  properties?.forEach?.((item) => {
    if (['dropdown_list', 'date_range'].includes(item.property_type)) {
      arrCodeMap[item.property_code] = item.property_code;
    }
  })
  Object.keys(newValues).forEach((key) => {
    if (!arrCodeMap[key]) {
      newValues[key] = newValues[key]?.[0] ?? newValues[key]
    }
  })
  return newValues;
}

// 合作内容值转换为formValue
const cooperationValueToFormValue = (
    cooperationValues: DemandCooperatePropertyValueDto[]
) => {
  const property_values =
      cooperationValues?.reduce((result, property) => {
        // 返回值默认是数组，如果长度为1，转换为单个值
        result[property.property_code] =
            property.value?.length > 1 ? property.value : property.value?.[0];
        return result || '';
      }, {}) || {};
      console.log(cooperationValues, 'value');
  return property_values;
};

// CooperationValueItem的property_values对齐schema
const alignPropertyValues = (
    property_values: DemandCooperatePropertyValueDto[],
    schema: DemandCooperatePropertiesVo
): DemandCooperatePropertyValueDto[] => {
  const standards =
      schema?.standards?.map((schemaItem) => {
        const standard =
            property_values.find((n) => n.property_code === schemaItem.code) || {};
        return {
          value: [],
          property_code: schemaItem.code,
          standard_schema: schemaItem,
          type: 1,
          ...standard,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  const properties =
      schema?.properties?.map((schemaItem) => {
        const property =
            property_values.find(
                (n) => n.property_code === schemaItem.property_code
            ) || {};
        return {
          value: [],
          property_code: schemaItem.property_code,
          property_schema: schemaItem,
          type: 0,
          ...property,
        } as DemandCooperatePropertyValueDto;
      }) || [];
  return standards.concat(properties);
};

// 根据property_type生成FormItemDescriptionsItemType
const propertyTypeToComponentConfig = (
    schemaItem: PropertyRefDto,
    formValues?: Record<string, any[]>,
    structuredVersion?: number
): FormItemDescriptionsItemType => {

  const options = structuredVersion < 1
      ? schemaItem?.property_options?.map(v => ({
        name:v,
        value: v,
      })) : getLogicOptions(schemaItem, formValues);

  let config = {};
  switch (schemaItem.property_type) {
    case 'text':
      config = {
        componentClass: 'textarea',
        size: 'medium',
        span: 2,
        rules: [
          {
            validator: (rule, value, callback) => {
              if (value?.length > 500) {
                callback('不能输入超过500个字符');
              } else {
                callback();
              }
            },
          },
        ],
        elementProps: {
          placeholder: '请输入',
          maxLength: 500,
        },
      };
      break;
    case 'dropdown_list':
      config = {
        componentClass: 'select',
        size: 'medium',
        rules: [
          ...getOptionsRule(options),
        ],
        elementProps: {
          allowClear: true,
          multiple: true,
          options,
          showComponent(value) {
            value = Array.isArray(value) ? value : [value];
            return value.join(',');
          },
          clearValueWhenOptionsEmpty: true,
        },
      };
      break;
    case 'dropdown_radio':
      config = {
        componentClass: 'select',
        size: 'medium',
        rules: [
          ...getOptionsRule(options),
        ],
        elementProps: {
          allowClear: true,
          multiple: false,
          options,
          showComponent(value) {
            value = Array.isArray(value) ? value : [value];
            return value.join(',');
          },
          clearValueWhenOptionsEmpty: true,
        },
      };
      break;
    case 'numeric':
      config = {
        componentClass: 'inputNumber',
        size: 'medium',
        rules: [
          {
            message: '不能输入负数',
            validator: (rule, value) => {
              if (value < 0) {
                return Promise.reject('不能输入负数');
              }
              return Promise.resolve();
            },
          },
          {
            message: '只能输入4位小数',
            validator: (rule, value) => {
              if (value && value.toString().split('.')[1]?.length > 4) {
                return Promise.reject('只能输入4位小数');
              }
              return Promise.resolve();
            },
          },
        ],
        elementProps: {
          toFixed: 4,
          placeholder: '请输入',
        },
      };
      break;
    case 'date_input':
      config = {
        componentClass: 'datePicker',
        size: 'medium',
        elementProps: {
          placeholder: '请选择日期',
        },
      };
      break;
    case 'date_range':
      config = {
        componentClass: 'datePicker',
        size: 'medium',
        span: 2,
        elementProps: {
          range: true,
          placeholder: ['开始日期', '结束日期'],
        },
      };
      break;
    case 'text_with_upload': {
      config = {
        componentClass: 'text_with_upload',
        elementProps: {},
        valueFormat: {
          /**
           * 外部value转为内部fieldValue
           * @param value 外部传入的value
           * @param fieldProps 字段属性
           * @returns 内部控件可用的fieldValue
           */
          valueToField: (value: any, fieldProps: any) => value?.[0] || {},
          /**
           * 内部fieldValue转为外部value
           * @param fieldValue 控件内部的值
           * @param fieldProps 字段属性
           * @returns 外部表单需要的value
           */
          fieldToValue: (fieldValue: any, fieldProps: any) => [fieldValue]
        },
      };
      break;
    }
    case 'input':
    default:
      config = {
        componentClass: 'input',
        size: 'medium',
        elementProps: {
          placeholder: '请输入',
          maxLength: 50,
        },
      };
  }
  const requiredRules = getRequiredRule(schemaItem, formValues);

  return {
    formItemKey: schemaItem.property_code,
    label: schemaItem.property_name,
    ...config,
    rules: [
      ...requiredRules,
      ...(config?.rules || []),
    ],
  };
};

const standardTypeToComponentConfig = (
    schemaItem: TemplateStandardItemDto,
    schemaStandards?: TemplateStandardItemDto[],
    formValues?: Record<string, any>,
): FormItemDescriptionsItemType => {
  const parentCode = `standardCode${schemaItem?.level - 1}`;
  // const getOptions = () => {
  //   return schemaItem?.standards?.filter((s) => {
  //     return schemaItem?.level > 1 ? formValues?.[parentCode]?.toString() === s.parent_code : true;
  //   })?.map((s) => ({
  //     ...s,
  //     name: s.name,
  //     value: s.code,
  //   })) || [];
  // };
  const options = getLogicOptions(schemaItem as any, formValues);

  const config = {
    componentClass: 'select' as any,
    size: 'medium',
    required: schemaItem?.required,
    rules: [
      ...getOptionsRule(options)
    ],
    elementProps: {
      multiple: false,
      allowClear: true,
      options: options,
      showComponent(v) {
        const value = Array.isArray(v) ? v : [v];
        const options = schemaItem?.standards?.filter((si) => {
          return value.includes(si.code);
        });
        return options?.map((opt) => opt.name).join('、');
      },
    },
  };

  return {
    formItemKey: schemaItem?.code,
    required: schemaItem?.required,
    label: schemaItem?.label,
    ...config,
  };
};

const CooperationPropertyForm = forwardRef(function (
    props: CooperationFormListProps,
    ref
) {
  const [dataList, setDataList] = useControllableValue<
      DemandCooperatePropertyValueDto[]
  >(props, {
    defaultValue: [],
  });
  // 外部数据变化，更新本地数据
  const [localDataList, setLocalDataList] = useState([]);

  const {
    view = false,
    schema: parentSchema = null,
    editType = 'default',
    structuredVersion,
  } = props;

  const [form] = Form.useForm();

  useEffect(() => {
    if (isEqual(dataList, localDataList)) {
      return;
    } else {
      form.setFieldsValue(cooperationValueToFormValue(dataList));
    }
  }, [dataList, localDataList]);

  const [disabledFields, setDisabledFields] = useState<string[]>([]);
  // useEffect(() => {
  //   // 编辑类型为退回商务确认报价确认节点时，获取不可编辑字段
  //   if (editType === 'returnToBusiness') {
  //     commonFilterOptions({ keys: ['cooperate_account_edit_disable'] }).then(
  //       (res: any) => {
  //         const temp =
  //           res?.data?.[0]?.options?.map?.((item) => item.value) || [];
  //         setDisabledFields(temp);
  //       }
  //     );
  //   }
  // }, [editType]);

  const [schema, setSchema] = useState<DemandCooperatePropertiesVo | null>(
      null
  );

  const localSchema = useMemo(() => {
    if (!dataList?.length) {
      return {};
    }
    const property_values = dataList || [];
    const standards =
        property_values
            .map((n) => n.standard_schema)
            .filter((n) => !!n)
            ?.map?.((conf) => {
          if (disabledFields.includes(conf?.code)) {
            conf.view = true;
          }
          return conf;
        }) || [];
    const properties =
        property_values
            .map((n) => n.property_schema)
            .filter((n) => !!n)
            ?.map?.((conf) => {
          if (disabledFields.includes(conf?.property_code)) {
            conf.view = true;
          }
          return conf;
        }) || [];
    return { properties, standards } as DemandCooperatePropertiesVo;
  }, [dataList, disabledFields]);

  useEffect(() => {
    if (!view && parentSchema) {
      // 跟新schema
      setSchema(parentSchema);
    } else {
      setSchema(localSchema);
    }
  }, [parentSchema, view, localSchema]);

  const mergeSchema = useMemoizedFn(() => {
    let isPropertiesDifferent = false;
    let isStandardsDifferent = false;

    if (schema?.properties?.length && localSchema?.properties?.length) {
      const localSchemaKeys = localSchema?.properties
          ?.map((n) => n?.property_code)
          .sort();
      const schemaKeys = schema?.properties?.map((n) => n.property_code).sort();
      isPropertiesDifferent =
          localSchemaKeys.join(',') !== schemaKeys.join(',');
    }

    if (schema?.standards?.length && localSchema?.standards?.length) {
      const localSchemaKeys = localSchema?.standards
          ?.map((n) => n?.code)
          .sort();
      const schemaKeys = schema?.standards?.map((n) => n.code).sort();
      isStandardsDifferent = localSchemaKeys.join(',') !== schemaKeys.join(',');
    }

    // 如果schema不一样，对齐数据的schema
    if (isPropertiesDifferent || isStandardsDifferent) {
      setDataList(alignPropertyValues(dataList, schema));
    }
  });


  useEffect(() => {
    if(!dataList?.length && schema?.properties?.length) {
      onFormChange({},{})
    }
  }, [schema, dataList]);

  // 数据和schema对齐,模型改变则清空，不再进行数据合并
  // useDebounceEffect(
  //     () => {
  //       mergeSchema();
  //     },
  //     [dataList, schema],
  //     {
  //       wait: 40,
  //     }
  // );

  const onFormChange = useMemoizedFn((changedValues, allValues) => {
    if (!schema) {
      return;
    }
    const newValues = formValueToCooperationValue(allValues, schema);
    setDataList(newValues);
    setLocalDataList(newValues);
  });

  const standardItems =
      schema?.standards?.map((schemaItem) => {
        return {
          formItemKey: schemaItem.code,
          // view = true 时全部只读，view = false 时单字段只读控制
          view: view ? true : !!schemaItem.view,
          ...standardTypeToComponentConfig(schemaItem, schema?.standards, form.getFieldsValue()),
        };
      }) || [];

  const propertyItems =
      schema?.properties?.map((schemaItem) => {
        return {
          formItemKey: schemaItem.property_code,
          // view = true 时全部只读，view = false 时单字段只读控制
          view: view ? true : !!schemaItem.view,
          ...propertyTypeToComponentConfig(schemaItem, form.getFieldsValue(), structuredVersion),
        };
      }) || [];

  const items = [...standardItems, ...propertyItems];

  useImperativeHandle(ref, () => ({
    validateFields: () => {
      form.clearValidate?.();
      return form.validateFields().catch((error) => {
        return Promise.reject(undefined);
      });
    },
  }));

  return (
      <div>
        {/* <Button type="primary" onClick={() => {
        console.log(form);
        form.validateFields().then((res) => {
          console.log('success', res);
        }).catch((error) => {
          console.log('error', error);
        });
      }}>
        获取表单值
      </Button> */}
        <Form
            form={form}
            onValuesChange={onFormChange}
            initialValues={{}}
            style={{ width: '100%' }}
        >
          {!!items?.length && (
              <FormItemDescriptions
                  lightBorder
                  className={cx('collapse-content-table')}
                  column={2}
                  items={items}
              />
          )}
        </Form>
      </div>
  );
});

export default CooperationPropertyForm;
